import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Loader2, Plus, Search } from 'lucide-react';
import { dbClient, normalizeRpcResponse } from '@/services/db-client';
import { apiClient } from '@/services/api-client';
import { cn } from '@/lib/utils';
import { formatUnknownError, senderLabel } from './commUi';
import { PunctualComposer } from './PunctualComposer';

interface BlastRow {
    id: string;
    name: string;
    channel: string;
    subject: string;
    body?: string | null;
    click_url?: string | null;
    image_url?: string | null;
    audience: string;
    only_email: string | null;
    status: string;
    created_at: string;
    finished_at: string | null;
    queued: number;
    sent: number;
    failed: number;
    opened: number;
    clicked: number;
}

interface RecipientRow {
    id: string;
    status: string;
    email: string | null;
    display_name: string | null;
    provider: string | null;
    error_code: string | null;
    error_detail: string | null;
    suppress_reason: string | null;
    sent_at: string | null;
    opened_at: string | null;
    clicked_at: string | null;
}

type ChannelFilter = 'all' | 'email' | 'push';
type StatusFilter = 'all' | 'sending' | 'sent' | 'failed';
type RecipientFilter = 'all' | 'queued' | 'out' | 'failed';

const chipClass = (active: boolean) =>
    cn(
        'rounded-full border px-3 py-1.5 text-sm',
        active
            ? 'border-primary bg-primary text-primary-foreground'
            : 'border-border bg-muted/40 text-muted-foreground hover:text-foreground'
    );

function pct(part: number, total: number): string {
    if (!total) return '—';
    return `${Math.round((Number(part) / Number(total)) * 100)}%`;
}

function blastStatusLabel(status: string): string {
    if (status === 'sent') return 'Enviado';
    if (status === 'failed') return 'No salió';
    return 'En curso';
}

function audienceLabel(row: BlastRow): string {
    if (row.audience === 'criteria') return 'Criterios de ese momento';
    if (row.audience === 'all') return 'Toda la audiencia';
    return 'Personas elegidas';
}

function deliveryStatusLabel(status: string): string {
    if (status === 'queued' || status === 'deferred') return 'En cola';
    if (status === 'failed') return 'No salió';
    if (status === 'suppressed') return 'No se envió';
    if (status === 'delivered') return 'Entregado';
    if (status === 'opened') return 'Abierto';
    if (status === 'clicked') return 'Clic';
    return 'Enviado';
}

function formatWhen(value: string | null): string {
    if (!value) return '—';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '—';
    return date.toLocaleString('es-ES', {
        day: '2-digit',
        month: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
    });
}

function reasonOf(row: RecipientRow): string {
    if (row.error_detail) return row.error_detail;
    if (row.error_code) return row.error_code;
    if (row.suppress_reason === 'consent') return 'Sin permiso de marketing';
    if (row.suppress_reason === 'unreachable') return 'Sin correo o sin aviso';
    if (row.suppress_reason) return row.suppress_reason;
    return senderLabel(row.provider);
}

function matchesRecipient(row: RecipientRow, filter: RecipientFilter): boolean {
    if (filter === 'all') return true;
    if (filter === 'queued') return row.status === 'queued' || row.status === 'deferred';
    if (filter === 'failed') return row.status === 'failed' || row.status === 'suppressed';
    return row.status === 'sent' || row.status === 'delivered' || row.status === 'opened' || row.status === 'clicked';
}

export function PunctualPanel() {
    const [composing, setComposing] = useState(false);
    const [notice, setNotice] = useState<string | null>(null);
    const [selectedId, setSelectedId] = useState<string | null>(null);
    const [search, setSearch] = useState('');
    const [channelFilter, setChannelFilter] = useState<ChannelFilter>('all');
    const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');

    const listQuery = useQuery({
        queryKey: ['admin', 'comm-blasts'],
        queryFn: async () => {
            const { data, error: rpcError } = await dbClient.rpc<BlastRow[]>('admin_list_comm_blasts');
            if (rpcError) throw rpcError;
            return normalizeRpcResponse(data);
        },
        refetchInterval: (query) => {
            const rows = query.state.data ?? [];
            const waiting = rows.some((row) => row.status === 'sending' || Number(row.queued) > 0);
            return waiting ? 15000 : false;
        },
    });

    const rows = listQuery.data ?? [];
    const waiting = rows.some((row) => row.status === 'sending' || Number(row.queued) > 0);
    const filtered = useMemo(() => {
        const needle = search.trim().toLowerCase();
        return rows.filter((row) => {
            if (channelFilter !== 'all' && row.channel !== channelFilter) return false;
            if (statusFilter !== 'all' && row.status !== statusFilter) return false;
            if (!needle) return true;
            const haystack = `${row.subject} ${row.only_email ?? ''}`.toLowerCase();
            return haystack.includes(needle);
        });
    }, [channelFilter, rows, search, statusFilter]);
    const selected = rows.find((row) => row.id === selectedId) ?? null;

    return (
        <div className="space-y-4">
            <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                    <h2 className="font-semibold">Campañas</h2>
                    <p className="text-sm text-muted-foreground">
                        Pulsa una fila para ver a quién salió y en qué estado está cada persona.
                    </p>
                </div>
                <button
                    type="button"
                    className="inline-flex items-center gap-1 rounded-lg bg-primary px-3 py-2 text-sm text-primary-foreground"
                    onClick={() => {
                        setNotice(null);
                        setComposing(true);
                    }}
                >
                    <Plus size={16} /> Nueva campaña
                </button>
            </div>

            {notice && <p className="text-sm text-foreground">{notice}</p>}
            {waiting && (
                <p className="text-sm text-muted-foreground">
                    Quien sigue en cola sale solo, como mucho en un cuarto de hora. No es un rechazo del envío.
                </p>
            )}

            <div className="flex flex-wrap gap-2">
                {(
                    [
                        ['all', 'Todas'],
                        ['sending', 'En curso'],
                        ['sent', 'Enviadas'],
                        ['failed', 'No salieron'],
                    ] as const
                ).map(([value, label]) => (
                    <button
                        key={value}
                        type="button"
                        className={chipClass(statusFilter === value)}
                        onClick={() => setStatusFilter(value)}
                    >
                        {label}
                    </button>
                ))}
                {(
                    [
                        ['all', 'Canal'],
                        ['email', 'Correo'],
                        ['push', 'Aviso'],
                    ] as const
                ).map(([value, label]) => (
                    <button
                        key={value}
                        type="button"
                        className={chipClass(channelFilter === value)}
                        onClick={() => setChannelFilter(value)}
                    >
                        {label}
                    </button>
                ))}
            </div>

            <div className="relative max-w-md">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <input
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Buscar por asunto o correo"
                    className="w-full rounded-lg border border-border bg-background py-2 pl-9 pr-3 text-sm"
                />
            </div>
            <p className="text-sm text-muted-foreground">
                Llegaron: el buzón lo aceptó. Apertura: también la cuenta el revisor del correo al descargar las imágenes.
                Pulsaron: alguien siguió el botón; es la cifra que más se acerca a una persona.
            </p>

            <div className="overflow-x-auto rounded-xl border border-border">
                {listQuery.isLoading && (
                    <div className="flex justify-center p-10">
                        <Loader2 className="animate-spin text-muted-foreground" />
                    </div>
                )}
                {listQuery.isError && (
                    <p className="p-4 text-sm text-destructive">
                        {formatUnknownError(listQuery.error, 'No se pudieron cargar las campañas')}
                    </p>
                )}
                {!listQuery.isLoading && !listQuery.isError && (
                    <table className="w-full min-w-[880px] text-sm">
                        <thead className="bg-muted/40 text-left text-muted-foreground">
                            <tr>
                                <th className="px-4 py-3 font-medium">Cuándo</th>
                                <th className="px-4 py-3 font-medium">Asunto</th>
                                <th className="px-4 py-3 font-medium">Canal</th>
                                <th className="px-4 py-3 font-medium">A quién</th>
                                <th className="px-4 py-3 font-medium text-right">En cola</th>
                                <th className="px-4 py-3 font-medium text-right">Llegaron</th>
                                <th className="px-4 py-3 font-medium text-right">Apertura</th>
                                <th className="px-4 py-3 font-medium text-right">Pulsaron</th>
                                <th className="px-4 py-3 font-medium text-right">No salieron</th>
                                <th className="px-4 py-3 font-medium">Estado</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filtered.length === 0 && (
                                <tr>
                                    <td colSpan={10} className="px-4 py-8 text-center text-muted-foreground">
                                        {rows.length === 0 ? 'Todavía no hay campañas.' : 'Ninguna campaña coincide con el filtro.'}
                                    </td>
                                </tr>
                            )}
                            {filtered.map((row) => {
                                const sent = Number(row.sent);
                                return (
                                    <tr
                                        key={row.id}
                                        onClick={() => setSelectedId((current) => (current === row.id ? null : row.id))}
                                        className={cn(
                                            'cursor-pointer border-t border-border hover:bg-muted/30',
                                            selectedId === row.id && 'bg-primary/5'
                                        )}
                                    >
                                        <td className="px-4 py-3 whitespace-nowrap">{formatWhen(row.created_at)}</td>
                                        <td className="px-4 py-3 font-medium">{row.subject}</td>
                                        <td className="px-4 py-3">{row.channel === 'push' ? 'Aviso' : 'Correo'}</td>
                                        <td className="max-w-[240px] truncate px-4 py-3" title={row.only_email ?? audienceLabel(row)}>
                                            {row.only_email || audienceLabel(row)}
                                        </td>
                                        <td className="px-4 py-3 text-right">{Number(row.queued)}</td>
                                        <td className="px-4 py-3 text-right">{sent}</td>
                                        <td className="px-4 py-3 text-right">{pct(row.opened, sent)}</td>
                                        <td className="px-4 py-3 text-right">{pct(row.clicked, sent)}</td>
                                        <td className="px-4 py-3 text-right">{Number(row.failed)}</td>
                                        <td className="px-4 py-3">{blastStatusLabel(row.status)}</td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                )}
            </div>

            {selected && <BlastDetail blast={selected} waiting={waiting} />}
            {composing && (
                <PunctualComposer
                    onCancel={() => setComposing(false)}
                    onSent={({ notice: nextNotice, blastId }) => {
                        setNotice(nextNotice);
                        setComposing(false);
                        if (blastId) setSelectedId(blastId);
                    }}
                />
            )}
        </div>
    );
}

function BlastDetail(props: { blast: BlastRow; waiting: boolean }) {
    const [filter, setFilter] = useState<RecipientFilter>('all');
    const [search, setSearch] = useState('');
    const recipientsQuery = useQuery({
        queryKey: ['admin', 'comm-blast-recipients', props.blast.id],
        queryFn: async () => {
            const res = await apiClient.post<{ recipients: RecipientRow[] }>('/ops/comm-blast', {
                action: 'recipients',
                blast_id: props.blast.id,
            });
            if (res.error) throw new Error(res.error.message);
            return res.data?.recipients ?? [];
        },
        refetchInterval: props.waiting ? 15000 : false,
    });
    const recipients = recipientsQuery.data ?? [];
    const visible = useMemo(() => {
        const needle = search.trim().toLowerCase();
        return recipients.filter((row) => {
            if (!matchesRecipient(row, filter)) return false;
            if (!needle) return true;
            return `${row.display_name ?? ''} ${row.email ?? ''}`.toLowerCase().includes(needle);
        });
    }, [filter, recipients, search]);

    return (
        <section className="space-y-3 rounded-xl border border-border bg-card p-4">
            <div className="flex flex-wrap items-baseline justify-between gap-2">
                <h3 className="font-semibold">{props.blast.subject}</h3>
                <p className="text-sm text-muted-foreground">
                    {props.blast.channel === 'push' ? 'Aviso' : 'Correo'} · {audienceLabel(props.blast)} ·{' '}
                    {formatWhen(props.blast.created_at)}
                </p>
            </div>
            {props.blast.body && (
                <div className="rounded-lg border border-border bg-background p-3 text-sm">
                    <p className="whitespace-pre-wrap">{props.blast.body}</p>
                    {props.blast.click_url && (
                        <p className="mt-2 text-muted-foreground">Botón: {props.blast.click_url}</p>
                    )}
                    {props.blast.image_url && (
                        <p className="mt-1 truncate text-muted-foreground">Imagen: {props.blast.image_url}</p>
                    )}
                </div>
            )}
            <div className="flex flex-wrap gap-2">
                {(
                    [
                        ['all', 'Todos'],
                        ['queued', 'En cola'],
                        ['out', 'Enviados'],
                        ['failed', 'No salieron'],
                    ] as const
                ).map(([value, label]) => (
                    <button key={value} type="button" className={chipClass(filter === value)} onClick={() => setFilter(value)}>
                        {label}
                    </button>
                ))}
            </div>
            <div className="relative max-w-md">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <input
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Buscar persona o correo"
                    className="w-full rounded-lg border border-border bg-background py-2 pl-9 pr-3 text-sm"
                />
            </div>
            {recipientsQuery.isLoading && (
                <div className="flex justify-center p-6">
                    <Loader2 className="animate-spin text-muted-foreground" />
                </div>
            )}
            {recipientsQuery.isError && (
                <p className="text-sm text-destructive">
                    {formatUnknownError(recipientsQuery.error, 'No se pudo cargar la lista de personas')}
                </p>
            )}
            {!recipientsQuery.isLoading && !recipientsQuery.isError && (
                <div className="overflow-x-auto">
                    <table className="w-full min-w-[720px] text-sm">
                        <thead className="text-left text-muted-foreground">
                            <tr>
                                <th className="py-2 pr-3 font-medium">Persona</th>
                                <th className="py-2 pr-3 font-medium">Correo</th>
                                <th className="py-2 pr-3 font-medium">Estado</th>
                                <th className="py-2 pr-3 font-medium">Enviado</th>
                                <th className="py-2 pr-3 font-medium">Apertura</th>
                                <th className="py-2 pr-3 font-medium">Clic</th>
                                <th className="py-2 font-medium">Remitente o motivo</th>
                            </tr>
                        </thead>
                        <tbody>
                            {visible.length === 0 && (
                                <tr>
                                    <td colSpan={7} className="py-6 text-center text-muted-foreground">
                                        Nadie coincide con el filtro.
                                    </td>
                                </tr>
                            )}
                            {visible.map((row) => (
                                <tr key={row.id} className="border-t border-border/60">
                                    <td className="py-2 pr-3">{row.display_name || 'Sin nombre'}</td>
                                    <td className="py-2 pr-3">{row.email || '—'}</td>
                                    <td className="py-2 pr-3">{deliveryStatusLabel(row.status)}</td>
                                    <td className="py-2 pr-3 whitespace-nowrap">{formatWhen(row.sent_at)}</td>
                                    <td className="py-2 pr-3 whitespace-nowrap">{formatWhen(row.opened_at)}</td>
                                    <td className="py-2 pr-3 whitespace-nowrap">{formatWhen(row.clicked_at)}</td>
                                    <td className="max-w-[280px] truncate py-2" title={reasonOf(row)}>
                                        {reasonOf(row)}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </section>
    );
}
